// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, type ReactNode } from 'react';

import { ApiRequestError, api } from '../api/client';
import type { Health, ProjectSummary } from '../api/types';
import { JobToaster } from '../components/JobToaster';
import { useAsync } from './useAsync';
import { useTheme } from './useTheme';
import { useJobs } from './JobsContext';
import { SurveyCountsProvider } from './SurveyCountsContext';
import { useAuth } from './AuthGate';
import { useCaseRole } from './CaseRoleContext';
import { caseBootAction, writeLastCase } from './cases';
import { ALL_CASES_PATH, displayProjectName } from './navigation';
import { CaseShellProvider, type CaseShell } from './bygning/CaseShellContext';
import styles from './AppShell.module.css';

/**
 * The case-wide wrapper both frames sit in — the Bygning shell (the six
 * modes) and the expert `ToolFrame`. It owns what is resolved once per case,
 * and hands it down through `CaseShellContext`:
 *
 * - the health handshake (`GET /health`, the cheap call the contract
 *   designates for it): a case that is gone sends the browser back to the
 *   case list (`caseBootAction`), a healthy one is remembered as the last;
 * - the display name (record name from `GET /project`, else the file name);
 * - the events connection and the running-job count (`useJobs`);
 * - the one `useTheme()` instance and the signed-in user;
 * - `SurveyCountsProvider`, which the case store and Segmentering call after
 *   a write (it re-reads the project record for the display name);
 * - the job toaster.
 *
 * The frames draw the chrome themselves (top bar, read-only notice).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { data: health, error } = useAsync<Health>((signal) => api.health(signal), []);
  useEffect(() => {
    if (!health && !error) return;
    const action = caseBootAction(
      health ? { ok: true } : { ok: false, status: error instanceof ApiRequestError ? error.status : undefined },
    );
    if (action === 'remember' && api.caseId) writeLastCase(api.caseId);
    // The case is gone (deleted, or a stale link): back to the list rather
    // than a screen of 404s and an events socket retrying forever.
    if (action === 'leave') window.location.replace(ALL_CASES_PATH);
  }, [health, error]);
  const project = useAsync<ProjectSummary>((signal) => api.projectSummary(signal), []);
  const { active, status } = useJobs();
  // The Bygning shell keeps its own summary (useCaseData), so a count refresh
  // has no badge here to update; only the project record is re-read.
  const surveyCounts = useMemo(() => ({ refresh: () => {}, refreshProject: project.reload }), [project.reload]);

  const theme = useTheme();
  const auth = useAuth();
  const role = useCaseRole();

  const shell = useMemo<CaseShell>(
    () => ({
      health,
      unreachable: Boolean(error),
      projectName: displayProjectName(project.data, health),
      connection: status,
      activeJobCount: active.length,
      theme,
      user: auth?.me.mode === 'server' ? auth.me.user : undefined,
      logout: auth ? () => void auth.logout() : undefined,
      viaShareLink: auth?.me.via === 'share_link',
      role,
    }),
    [health, error, project.data, status, active.length, theme, auth, role],
  );

  return (
    <CaseShellProvider value={shell}>
      <div className={styles.shell}>
        <SurveyCountsProvider value={surveyCounts}>{children}</SurveyCountsProvider>
      </div>
      <JobToaster />
    </CaseShellProvider>
  );
}
